import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getAccessiblePaths, type NavTabPath } from "../access";
import { useAppData } from "../AppData";
import {
  CalculatorIcon,
  EyeIcon,
  NotebookIcon,
  PenIcon,
  SettingsIcon,
} from "./icons";

type Tab = {
  path: NavTabPath;
  label: string;
  icon: () => JSX.Element;
};

const TABS: Tab[] = [
  { path: "/", label: "Заявка", icon: () => <PenIcon size={24} /> },
  { path: "/calc", label: "Подсчёт", icon: () => <CalculatorIcon size={24} /> },
  { path: "/views", label: "Просмотры", icon: () => <EyeIcon size={24} /> },
  { path: "/report", label: "Отчёт", icon: () => <NotebookIcon size={24} /> },
  { path: "/settings", label: "Настройки", icon: () => <SettingsIcon size={24} /> },
];

function isActive(pathname: string, tabPath: string): boolean {
  if (tabPath === "/") return pathname === "/";
  return pathname === tabPath || pathname.startsWith(`${tabPath}/`);
}

/** Скрывает меню при скролле вниз, показывает при скролле вверх. */
function useHideOnScroll(): boolean {
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);

  useEffect(() => {
    lastY.current = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY.current;
        // У самого верха меню всегда видно.
        if (y < 24) {
          setVisible(true);
        } else if (Math.abs(delta) > 6) {
          setVisible(delta < 0);
        }
        lastY.current = y;
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return visible;
}

export function BottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visible = useHideOnScroll();
  const { me, operations, loading } = useAppData();

  // Пока грузятся права — не показываем пункты, чтобы не мелькали закрытые вкладки.
  const tabs = loading
    ? []
    : (() => {
        const allowed = new Set(getAccessiblePaths(me, operations));
        return TABS.filter((tab) => allowed.has(tab.path));
      })();

  return (
    <nav className={`bottom-nav${visible ? "" : " bottom-nav--hidden"}`}>
      <div className="bottom-nav__bar">
        {tabs.map((tab) => {
          const active = isActive(pathname, tab.path);
          return (
            <button
              key={tab.path}
              type="button"
              className={`bottom-nav__item${active ? " bottom-nav__item--active" : ""}`}
              onClick={() => navigate(tab.path)}
            >
              <span className="bottom-nav__icon">{tab.icon()}</span>
              <span className="bottom-nav__label">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
